import React, { useEffect, useMemo, useRef, useState } from "react";
import { Headphones, Brain, Speaker, Phone, Radio, ChevronDown } from "lucide-react";
import { useT } from "../context/LanguageContext";

const stageIcons = { stt: Headphones, llm: Brain, tts: Speaker, voice: Phone };
const accentMap = {
  coral: "#FF7A7A",
  cyan: "#4B6FD4",
  navy: "#2B50B8",
  teal: "#14B8A6",
  violet: "#8B5CF6",
};

const StackSection = () => {
  const t = useT();
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const pipelineStages = useMemo(() => [
    { key: "stt", label: t("stack.stage.stt.label"), sublabel: "STT", description: t("stack.stage.stt.description"), providers: ["Whisper", "Deepgram", "Google STT", "Azure STT"], accent: "coral" },
    { key: "llm", label: t("stack.stage.llm.label"), sublabel: "LLM", description: t("stack.stage.llm.description"), providers: ["GPT-4o", "Claude Sonnet", "Gemini 2.5", "Llama 3"], accent: "teal" },
    { key: "tts", label: t("stack.stage.tts.label"), sublabel: "TTS", description: t("stack.stage.tts.description"), providers: ["ElevenLabs", "OpenAI TTS", "Azure Speech", "Kokoro"], accent: "violet" },
    { key: "voice", label: t("stack.stage.voice.label"), sublabel: "Voice / SIP", description: t("stack.stage.voice.description"), providers: ["NetGSM", "Verimor", "Twilio", "Vonage"], accent: "cyan" },
  ], [t]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setVisible(true), obs.disconnect()),
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const t = setInterval(
      () => setActiveIdx((i) => (i + 1) % pipelineStages.length),
      1800
    );
    return () => clearInterval(t);
  }, [visible, pipelineStages.length]);

  return (
    <section
      id="stack"
      ref={ref}
      className={`section-fade ${visible ? "visible" : ""} py-20 px-6`}
      style={{ minHeight: "100vh", background: "var(--section-surface-3)" }}
    >
      <div className="max-w-6xl mx-auto w-full">
        {/* Title and description - full width on top */}
        <div className="flex flex-col items-center text-center mb-12">
          <h2
            className="font-bold tracking-tight leading-[1.05]"
            style={{ color: "var(--fg)", fontSize: "clamp(2rem, 4.2vw, 3.25rem)" }}
          >
            {t("stack.title1")} {" "}
            <span
              className="italic inline-block"
              style={{
                background:
                  "linear-gradient(90deg, var(--brand-coral), var(--brand-cyan))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                color: "transparent",
                paddingRight: "0.08em",
              }}
            >
              {t("stack.title2")}
            </span>{" "}
            {t("stack.title3")}
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed max-w-2xl" style={{ color: "var(--fg)", opacity: 0.92 }}>
            {t("stack.subtitle")}
          </p>
          <div className="mt-7 flex items-center gap-2 text-[12px]" style={{ color: "var(--muted)" }}>
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: "var(--brand-coral)", animation: "pulseDot 1.5s ease-in-out infinite" }}
            />
            <span>{t("stack.hint")}</span>
          </div>
          <style>{`@keyframes pulseDot { 0%,100% { transform: scale(1); opacity:1; } 50% { transform: scale(1.6); opacity:0.5; } }`}</style>
        </div>

        {/* Video and pipeline animation side by side */}
        <div className="grid lg:grid-cols-2 gap-10 items-stretch">
          {/* Stack visual image */}
          <div className="rounded-2xl overflow-hidden relative" style={{ border: "1px solid var(--border)", minHeight: "400px" }}>
            <img
              src="/stack.png"
              alt="Stack visual"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

        {/* Pipeline column - vertical with flowing connector */}
        <div className="relative">
          {/* Inbound card */}
          <div
            className="p-5 rounded-2xl mb-4 neon-box"
            style={{
              background: "var(--card-bg)",
              border: "1px dashed var(--border)",
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--brand-cyan)" }}
                >
                  <Radio size={18} />
                </div>
                <div>
                  <div
                    className="text-[10.5px] uppercase tracking-[0.18em] font-semibold"
                    style={{ color: "var(--fg)", opacity: 0.72 }}
                  >
                    {t("stack.inboundLabel")}
                  </div>
                  <div className="text-[15px] font-semibold" style={{ color: "var(--fg)" }}>
                    {t("stack.inboundValue")}
                  </div>
                </div>
              </div>
              <span className="text-[11px] px-2 py-1 rounded-md" style={{ background: "var(--bg)", color: "var(--muted)", border: "1px solid var(--border)" }}>
                + giden · widget
              </span>
            </div>
          </div>

          {/* Pipeline stages list */}
          <div className="relative">
            {/* Animated flow line on the far left */}
            <div
              className="absolute left-[28px] top-0 bottom-0 w-px"
              style={{ background: "var(--border)" }}
            />
            <div
              className="absolute left-[28px] w-px overflow-hidden"
              style={{ top: 0, bottom: 0 }}
            >
              <div
                className="w-full"
                style={{
                  height: "40%",
                  background:
                    "linear-gradient(180deg, transparent, #4B6FD4, #FF7A7A, #14B8A6, transparent)",
                  animation: "flowLine 4s linear infinite",
                }}
              />
            </div>
            <style>{`@keyframes flowLine { 0% { transform: translateY(-100%); } 100% { transform: translateY(250%); } }`}</style>

            {pipelineStages.map((stage, idx) => {
              const Icon = stageIcons[stage.key];
              const isActive = activeIdx === idx;
              const accent = accentMap[stage.accent];
              return (
                <div key={stage.key} className="relative pl-14 pb-4">
                  {/* Node dot on the line */}
                  <span
                    className="absolute left-[22px] top-7 w-[14px] h-[14px] rounded-full transition-all duration-300"
                    style={{
                      background: isActive ? accent : "var(--bg)",
                      border: `2px solid ${accent}`,
                    }}
                  />
                  <div
                    className="rounded-2xl p-5 transition-all duration-500 neon-box"
                    style={{
                      background: isActive
                        ? `${accent}08`
                        : "var(--card-bg)",
                      border: `1px solid ${isActive ? accent : "var(--border)"}`,
                      transform: isActive ? "translateX(4px)" : "translateX(0)",
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center text-[11px] font-bold"
                          style={{
                            background: `${accent}16`,
                            color: accent,
                            fontFamily: "ui-monospace, monospace",
                          }}
                        >
                          {stage.sublabel.slice(0, 3)}
                        </div>
                        <div>
                          <div
                            className="text-[10.5px] uppercase tracking-[0.18em] font-semibold"
                            style={{ color: "var(--fg)", opacity: 0.72 }}
                          >
                            {stage.sublabel}
                          </div>
                          <div className="text-[15.5px] font-semibold" style={{ color: "var(--fg)" }}>
                            {stage.label}
                          </div>
                        </div>
                      </div>
                      <Icon size={18} style={{ color: "var(--muted)" }} />
                    </div>
                    <p className="text-[13px] leading-relaxed mb-3" style={{ color: "var(--fg)", opacity: 0.85 }}>
                      {stage.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {stage.providers.map((p) => (
                        <span
                          key={p}
                          className="text-[11px] px-2 py-1 rounded-md"
                          style={{
                            background: `${accent}12`,
                            color: accent,
                            border: `1px solid ${accent}35`,
                          }}
                        >
                          {p}
                        </span>
                      ))}
                      <span
                        className="text-[11px] px-2 py-1 rounded-md inline-flex items-center gap-1"
                        style={{ color: "var(--muted)" }}
                      >
                        <ChevronDown size={12} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Live production agent */}
          <div
            className="mt-2 p-5 rounded-2xl flex items-center justify-between neon-box"
            style={{
              background:
                "linear-gradient(135deg, rgba(75,111,212,0.14), rgba(255,122,122,0.12), rgba(20,184,166,0.12))",
              border: "1px solid rgba(75,111,212,0.24)",
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: "var(--brand-navy)", color: "#ffffff" }}
              >
                <Phone size={16} />
              </div>
              <div>
                <div
                  className="text-[10.5px] uppercase tracking-[0.18em] font-semibold"
                  style={{ color: "var(--fg)", opacity: 0.72 }}
                >
                  {t("stack.production")}
                </div>
                <div className="text-[15px] font-semibold" style={{ color: "var(--fg)" }}>
                  {t("stack.productionValue")}
                </div>
              </div>
            </div>
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] px-2 py-1 rounded-full"
              style={{ background: "rgba(16,185,129,0.15)", color: "#10B981" }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#10B981", animation: "pulseDot 1.5s infinite" }}
              />
              {t("stack.live")}
            </span>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
};

export default StackSection;
